import type { HttpPost } from '@/shared/infra/http'
import { emitFunnelEvent } from './emit-funnel-event'
import { resolveScreenKey, type ScreenKey } from './screen-registry'

export const PRESENCE_THROTTLE_MS = 30 * 60 * 1000
export const SCREEN_VIEW_THROTTLE_MS = 30 * 1000

export const PRESENCE_THROTTLE_KEY = 'bb_presence_last_emit'
export const SCREEN_THROTTLE_KEY_PREFIX = 'bb_screen_last_emit:'

export type RouterLike = {
  state: { location: { pathname: string } }
  subscribe: (listener: (state: { location: { pathname: string } }) => void) => () => void
}

function readThrottle(key: string): number | null {
  try {
    if (typeof window === 'undefined') return null
    const raw = window.sessionStorage.getItem(key)
    if (!raw) return null
    const parsed = Number(raw)
    return Number.isFinite(parsed) ? parsed : null
  } catch {
    return null
  }
}

function writeThrottle(key: string, at: number): void {
  try {
    if (typeof window === 'undefined') return
    window.sessionStorage.setItem(key, String(at))
  } catch {
    return
  }
}

function passesThrottle(key: string, windowMs: number, now: number): boolean {
  const last = readThrottle(key)
  if (last !== null && now - last < windowMs) return false
  writeThrottle(key, now)
  return true
}

export function emitColdStartPresence(httpClient: HttpPost): void {
  writeThrottle(PRESENCE_THROTTLE_KEY, Date.now())
  void emitFunnelEvent({ eventType: 'app_opened', trigger: 'cold_start', httpClient })
}

export function emitForegroundPresence(httpClient: HttpPost): void {
  if (!passesThrottle(PRESENCE_THROTTLE_KEY, PRESENCE_THROTTLE_MS, Date.now())) return
  void emitFunnelEvent({ eventType: 'app_opened', trigger: 'foreground', httpClient })
}

export function startPresenceTracking(httpClient: HttpPost): () => void {
  if (typeof document === 'undefined') return () => undefined
  const onVisibilityChange = (): void => {
    if (document.visibilityState !== 'visible') return
    emitForegroundPresence(httpClient)
  }
  document.addEventListener('visibilitychange', onVisibilityChange)
  return () => document.removeEventListener('visibilitychange', onVisibilityChange)
}

export function startScreenViewTracking(router: RouterLike, httpClient: HttpPost): () => void {
  let previousScreen: ScreenKey | undefined

  const report = (pathname: string): void => {
    const screen = resolveScreenKey(pathname)
    if (!screen) return
    if (!passesThrottle(`${SCREEN_THROTTLE_KEY_PREFIX}${screen}`, SCREEN_VIEW_THROTTLE_MS, Date.now())) {
      return
    }
    const emittedFrom = previousScreen === screen ? undefined : previousScreen
    previousScreen = screen
    void emitFunnelEvent({
      eventType: 'screen_viewed',
      screen,
      previousScreen: emittedFrom,
      httpClient,
    })
  }

  report(router.state.location.pathname)
  return router.subscribe((state) => report(state.location.pathname))
}
